/**
 * Theming props for the Notifications Widget.
 * These map to CSS custom properties used internally.
 */
export interface NotificationsThemingProps {
  /** Container background color */
  notifBackgroundColor?: string;
  /** Primary text color */
  notifTextColor?: string;
  /** Secondary text color */
  notifSecondaryTextColor?: string;
  /** Border color */
  notifBorderColor?: string;
  /** Container border radius */
  notifBorderRadius?: string;
  /** Container padding */
  notifPadding?: string;
  /** Container box shadow */
  notifShadow?: string;
  /** Primary color */
  notifPrimaryColor?: string;
  /** Primary hover color */
  notifPrimaryHover?: string;
  /** Accent color (CSS) */
  notifAccentColor?: string;
  /** Notification item background */
  notifItemBg?: string;
  /** Notification item hover background */
  notifItemHoverBg?: string;
  /** Notification item border */
  notifItemBorder?: string;
  /** Notification item border radius */
  notifItemRadius?: string;
  /** Notification item box shadow */
  notifItemShadow?: string;
  /** Notification item padding */
  notifItemPadding?: string;
  /** Notification item gap */
  notifItemGap?: string;
  /** Unread notification background */
  notifUnreadBg?: string;
  /** Unread notification border */
  notifUnreadBorder?: string;
  /** Unread indicator color */
  notifUnreadIndicator?: string;
  /** Avatar size */
  notifAvatarSize?: string;
  /** Avatar border radius */
  notifAvatarRadius?: string;
  /** Avatar border */
  notifAvatarBorder?: string;
  /** Icon size */
  notifIconSize?: string;
  /** Icon color */
  notifIconColor?: string;
  /** Badge background */
  notifBadgeBg?: string;
  /** Badge text color */
  notifBadgeColor?: string;
  /** Badge size */
  notifBadgeSize?: string;
  /** Font family */
  notifFontFamily?: string;
  /** Title font size */
  notifTitleSize?: string;
  /** Title font weight */
  notifTitleWeight?: string;
  /** Body text font size */
  notifBodySize?: string;
  /** Timestamp font size */
  notifTimestampSize?: string;
  /** Empty state icon color */
  notifEmptyIconColor?: string;
  /** Empty state text color */
  notifEmptyTextColor?: string;
  /** Empty state icon size */
  notifEmptyIconSize?: string;
  /** Divider color */
  notifDividerColor?: string;
  /** Header background */
  notifHeaderBg?: string;
  /** Header border */
  notifHeaderBorder?: string;
  /** Button background */
  notifButtonBg?: string;
  /** Button text color */
  notifButtonColor?: string;
  /** Button border radius */
  notifButtonRadius?: string;
  /** Transition duration */
  notifTransitionDuration?: string;
  /** Hover scale factor */
  notifHoverScale?: string;
  /** Loader/spinner color */
  notifLoaderColor?: string;
}

/**
 * Core configuration props for the Notifications Widget.
 */
export interface NotificationsWidgetProps extends NotificationsThemingProps {
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** JWT / API key for authenticated access (sent via postMessage, never in URL) */
  authToken?: string;
  /** User ID for notification lookup */
  userId?: string;
  /** Theme preset. Default: "light" */
  theme?: "light" | "dark";
  /** Maximum number of notifications. Default: 10 */
  maxNotifications?: number;
  /** Enable auto-refresh (every 30 s). Default: true */
  autoRefresh?: boolean;
  /** Title shown in empty state. Default: "You're all caught up" */
  emptyTitle?: string;
  /** Subtitle shown in empty state. Default: "No notifications right now" */
  emptySubtitle?: string;
  /** Accent color name. Default: "blue" (light) / "gray" (dark) */
  accentColor?: string;
  /** Icon for empty state. Default: "bell" */
  emptyIcon?: "bell" | "gift" | "activity" | "trophy";
  /** Widget width CSS value. Default: "100%" */
  width?: string;
  /** Widget height CSS value. Default: "400px" */
  height?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: { widget: string; theme: string }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
}

/**
 * Mapping from camelCase theming prop names to the kebab-case query
 * parameter names used by the notifications widget iframe URL.
 */
export const NOTIF_THEMING_PROP_TO_PARAM: Record<
  keyof NotificationsThemingProps,
  string
> = {
  notifBackgroundColor: "notif-background-color",
  notifTextColor: "notif-text-color",
  notifSecondaryTextColor: "notif-secondary-text-color",
  notifBorderColor: "notif-border-color",
  notifBorderRadius: "notif-border-radius",
  notifPadding: "notif-padding",
  notifShadow: "notif-shadow",
  notifPrimaryColor: "notif-primary-color",
  notifPrimaryHover: "notif-primary-hover",
  notifAccentColor: "notif-accent-color",
  notifItemBg: "notif-item-bg",
  notifItemHoverBg: "notif-item-hover-bg",
  notifItemBorder: "notif-item-border",
  notifItemRadius: "notif-item-radius",
  notifItemShadow: "notif-item-shadow",
  notifItemPadding: "notif-item-padding",
  notifItemGap: "notif-item-gap",
  notifUnreadBg: "notif-unread-bg",
  notifUnreadBorder: "notif-unread-border",
  notifUnreadIndicator: "notif-unread-indicator",
  notifAvatarSize: "notif-avatar-size",
  notifAvatarRadius: "notif-avatar-radius",
  notifAvatarBorder: "notif-avatar-border",
  notifIconSize: "notif-icon-size",
  notifIconColor: "notif-icon-color",
  notifBadgeBg: "notif-badge-bg",
  notifBadgeColor: "notif-badge-color",
  notifBadgeSize: "notif-badge-size",
  notifFontFamily: "notif-font-family",
  notifTitleSize: "notif-title-size",
  notifTitleWeight: "notif-title-weight",
  notifBodySize: "notif-body-size",
  notifTimestampSize: "notif-timestamp-size",
  notifEmptyIconColor: "notif-empty-icon-color",
  notifEmptyTextColor: "notif-empty-text-color",
  notifEmptyIconSize: "notif-empty-icon-size",
  notifDividerColor: "notif-divider-color",
  notifHeaderBg: "notif-header-bg",
  notifHeaderBorder: "notif-header-border",
  notifButtonBg: "notif-button-bg",
  notifButtonColor: "notif-button-color",
  notifButtonRadius: "notif-button-radius",
  notifTransitionDuration: "notif-transition-duration",
  notifHoverScale: "notif-hover-scale",
  notifLoaderColor: "notif-loader-color",
};

